<template>
  <section class="projects">
    <article
      v-for="(project, key) in projects"
      class="project js-project"
      @mouseenter="onProjectHover"
      @mouseleave="resetMousePos"
      @click="viewProject(project.slug)"
      :key="key"
    >
      <div class="project__body js-project-body">
        <h1 class="project__title">{{ project.title.rendered }}</h1>
        <h2 class="projet__type">{{ project.category }}</h2>
      </div>
      <div class="project__thumbnail-wrapper">
        <img
          :src="project.better_featured_image.source_url"
          :alt="project.title.rendered"
          class="project__thumbnail"
          :class="
            project.CFS.thumbnailAlignement &&
            `project__thumbnail--${project.CFS.thumbnailAlignement[0]}`
          "
        />
      </div>
    </article>
    <div class="projects__transition js-project-transition"></div>
  </section>
</template>

<script>
import * as UDOM from "@/util/UDOM";
import EventBus from "@/util/EventBus";
import * as UMath from "@/util/UMath";
import { mapState } from "vuex";

export default {
  name: "project-list-view",
  props: {
    mouse: Object,
    mouseNoI: Object,
    isDesktop: Boolean,
    scrollContent: Function,
  },
  data() {
    return {
      $transition: null,
      $projectHovered: null,
      mI: {
        x: 0,
        y: 0,
      },
    };
  },

  computed: {
    numberOfItemToAnimate() {
      return 2;
    },

    ...mapState({
      projects: (state) => state.projects,
    }),
  },

  mounted() {
    EventBus.$on("rAF", this.rAF);

    this.$transition = this.$el.querySelector(".js-project-transition");

    TweenMax.set(this.$transition, {
      scaleX: 0,
    });
  },

  destroyed() {
    EventBus.$off("rAF");
  },

  methods: {
    onProjectHover(e) {
      this.$projectHovered = e.currentTarget;

      // reset MouseInertia to center
      this.mI = {
        x: window.innerWidth / 2,
        y: this.$projectHovered.offsetHeight,
      };
    },

    updateMousePos(e) {
      const $projectBody =
        this.$projectHovered.querySelector(".js-project-body");
      const sensibility = 0.3;

      const center = {
        x: window.innerWidth / 2,
        y: this.$projectHovered.offsetHeight,
      };

      const { x, y } = this.mouseNoI;

      // create own MouseInertia to avoid jump
      this.mI = {
        x: UMath.getInertia(x, this.mI.x, this.$parent.MOUSE_INERTIA),
        y: UMath.getInertia(y, this.mI.y, this.$parent.MOUSE_INERTIA),
      };

      let dX;
      let dY;
      let offsetX;
      let offsetY;

      dX = this.mI.x - center.x;
      dY = this.mI.y - center.y;

      offsetX = dX * sensibility;
      offsetY = dY * sensibility;

      TweenMax.set($projectBody, {
        x: offsetX,
        y: offsetY,
        force3D: true,
      });
    },

    resetMousePos(e) {
      const $element = e.currentTarget;
      const $project = UDOM.closest($element, ".js-project");

      const $projectBody = $project.querySelector(".js-project-body");

      TweenMax.to($projectBody, 0.8, {
        x: 0,
        y: 0,
        ease: Quart.easeOut,
        force3D: true,
      });

      this.$projectHovered = null;
    },

    enter(done) {
      const tl = new TimelineMax({
        onComplete: done,
      });

      const projectItems = this.$el.querySelectorAll(".js-project");

      tl.set(projectItems, {
        y: 35,
        autoAlpha: 0,
      });

      tl.staggerTo(
        projectItems,
        0.5,
        {
          y: 0,
          autoAlpha: 1,
          force3D: true,
          easing: Quart.easeOut,
        },
        0.15
      );
    },

    leave(done) {
      if (this.$route.name === "Project") {
        done();
      } else {
        TweenMax.set(this.$transition, { autoAlpha: 0 });
        TweenMax.to(this.$el, 0.35, {
          autoAlpha: 0,
          ease: Quad.easeOut,
          force3D: true,
          onComplete: done,
        });
      }
    },

    viewProject(slug) {
      const duration = this.isDesktop ? 0.8 : 0.6;

      TweenMax.to(this.$transition, duration, {
        scaleX: 1,
        force3D: true,
        ease: Quart.easeOut,
        onComplete: this.onTransitonComplete,
        onCompleteScope: this,
        onCompleteParams: [slug],
      });
    },

    onTransitonComplete(slug) {
      window.scrollTo(0, 0);
      this.$parent.resetScroll();
      this.$router.push({ name: "Project", params: { slug: slug } });
    },

    rAF() {
      if (this.$projectHovered && this.isDesktop) {
        this.updateMousePos();
      }
      if (this.isDesktop) {
        this.scrollContent();
      }
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
@import "~include-media/dist/_include-media.scss";

.projects {
  padding: 2em 0 6em;
}

.projects__transition {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background-color: $white;
  transform: translate3d(0, 0, 0);
  transform-origin: 100% 50%;
  z-index: 20;
}

.project {
  position: relative;
  max-width: 810px;
  min-height: 400px;
  margin: 0 auto 1em;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    .project__thumbnail-wrapper {
      transform: scale(1) translateZ(0);
      opacity: 1;
      transition-delay: 0s;
    }

    .project__body {
      color: $white;
      mix-blend-mode: difference;
      transition-duration: 0.2s;
    }
  }
}

.project__thumbnail-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  transform: scale(1.025) translateZ(0);
  opacity: 0;
  transition: opacity 0.6s $easeOutQuad, transform 0.6s $easeOutQuart;
}

.project__thumbnail {
  object-fit: cover;
  max-width: 100%;
  width: 810px;
  height: 400px;
}

.project__body {
  max-width: 440px;
  z-index: 10;
  color: $text-color;
  transition: color 0s $easeOutQuad;
}

.project__title,
.projet__type {
  margin: 0;
  line-height: 1.2;
}

.project__title {
  font-weight: 800;
  font-size: 64px;
  line-height: 1;
}

.projet__type {
  font-size: 20px;
  margin-top: 0.4em;
}

@include media("<=tablet") {
  .project {
    max-width: 600px;
    min-height: 300px;
    margin-bottom: 58px;

    &:hover {
      .project__thumbnail-wrapper {
        transform: none;
        opacity: 1;
        transition-delay: 0s;
      }

      .project__body {
        mix-blend-mode: unset;
        transition-duration: 0s;
      }
    }
  }

  .project__title {
    font-size: 48px;
  }

  .projet__type {
    font-size: 18px;
  }

  .project__thumbnail-wrapper {
    left: -16px;
    width: calc(100% + 32px);
    overflow: hidden;
  }

  .project__thumbnail {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .project__thumbnail--top {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, 0);
  }

  .project__thumbnail-wrapper {
    transform: scale(1);
    opacity: 1;
    transition: none;
  }

  .project__body {
    color: $white;
  }
}
</style>



// WEBPACK FOOTER //
// ProjectListView.vue?16010d22